import {HandleReturnType} from '../../domain.js';
import {AgmPeer, Interest, StreamId, StreamPeerInfo, Subscription, SubscriptionId} from '../../state/types/agm.js';
import {DomainKeys, Peer, State} from '../../state/types/state.js';
import {
    byId,
    byIdInDomain,
    byIdInDomainThrows,
    byIdThrows,
    isLocalPeer,
    setPeer,
    updatePeer
} from '../../state/peers.js';
import {NodeAddress, PeerId, Source} from '../../state/types/common.js';
import * as msg from './messages.js';
import {reason, Reason, requestToReason, throwReason} from '../../reason.js';
import {
    AcceptedRequest,
    DropSubscriptionRequest,
    PostRequest,
    PublishRequest,
    SubscribeRequest,
    UnsubscribeRequest
} from './types/requests.js';
import {error, isLocalSource, success, unicast} from '../../common/messages.js';
import {nodeId, requestId} from '../../common/ids.js';
import {exceptionInfo} from '../../common/types.js';
import {nodeToAddress, peerToAddress} from '../../address.js';
import {validate} from './utilities.js';
import {getLogger} from '../../logger.js';
import {agmDomainUri, agmInvalidSubscription, agmSubscriptionFailure, reasonBadDrop} from './constants.js';
import {Draft, produce} from 'immer';
import {CommandType, MessageType, SendMessageAction} from '../../node.js';
import {Accepted, DropSubscription, Post, Publish, Subscribe} from './types/messages.js';
import {Id} from '../../state/types/mthds.js';

function cancelSubscriptionsFrom(state: State, subscriptionId: SubscriptionId, subscription: Subscription, subscriberId: PeerId, reason: Reason): HandleReturnType {
    const {server: serverId, stream: streamId, method_id: methodId} = subscription;
    const peer = produce(byIdThrows(state, serverId), removeInterestAndStream(subscriptionId, subscriberId, streamId));
    state = setPeer(state, serverId, peer);

    if (isLocalPeer(peer)) {
        return [state, [msg.removeInterest(peer.source, subscriptionId, serverId, methodId, subscriberId, reason)]];
    }
    // the remote servers will receive their own when the peer is removed on their node
    return [state, []];

}

/**
 * Cancels all subscriptions made by peer with subscriber-id and generates messages for the servers.
 * @return [state, [messages]]
 */
export function cancelSubscriptions(state: State, subscriber: Peer, reason: Reason): HandleReturnType {
    const subscriptions = subscriber[DomainKeys.Agm]?.subscriptions;
    const subscriberId = subscriber.id;
    const [newState, messages] =
        Object.entries(subscriptions || {} as Record<string, Subscription>)
            .reduce(([s, m], [subscriptionId, subscription]) => {
                const [ns, nm] = cancelSubscriptionsFrom(s, subscriptionId, subscription, subscriberId, reason);
                return [ns, m.concat(nm)];
            }, [state, []] as HandleReturnType);
    return [newState, messages];
}

function collectSubscriptions(subscriptions: Record<SubscriptionId, Subscription>, serverId: PeerId, methodIds?: Set<Id>): { removed: Record<SubscriptionId, Subscription>, remaining: Record<SubscriptionId, Subscription> } {
    return Object.entries(subscriptions).reduce((result, [subId, sub]) => {
        if ((serverId === sub.server)
            && ((!methodIds || methodIds.size === 0) || methodIds.has(sub.method_id))) {
            result.removed[subId] = sub;
        } else {
            result.remaining[subId] = sub;
        }
        return result;
    }, {removed: {}, remaining: {}});
}

/*Cancels all interest that subscriber-id might have on server-id methods

  Returns [state [message]]*/
export function cancelInterest(state: State, subscriber: Peer, server: Peer, methodIds: Set<Id> | undefined, reason): HandleReturnType {
    const serverId = server.id;
    const {
        removed,
        remaining
    } = collectSubscriptions(subscriber[DomainKeys.Agm]?.subscriptions || {}, serverId, methodIds);
    const stateWithoutInterests = Object.entries(removed).reduce((s, [subId, sub]) => {
        let messages;
        [state, messages] = cancelSubscriptionsFrom(state, subId, sub, subscriber.id, reason);
        return state;
    }, state);
    if (Object.keys(removed).length > 0) {
        // todo trace unsubscription
        return [updatePeer(stateWithoutInterests, subscriber.id, (p) => {
            if (Object.keys(remaining).length > 0) {
                p[DomainKeys.Agm]!.subscriptions = remaining;
            } else if (p[DomainKeys.Agm]) {
                delete p[DomainKeys.Agm].subscriptions;
            }
        }), isLocalSource(subscriber.source)
            ? Object.keys(removed).map((k) => msg.subscriptionCancelled(subscriber.source, k, subscriber.id, reason))
            : []]
    }
    return [state, []];
}

type RemoteSubscribeRequest = Subscribe & { subscriptionId: SubscriptionId };

function addInterest(caller: AgmPeer, state: State, request: RemoteSubscribeRequest): HandleReturnType {
    const {
        peer_id: peerId,
        server_id: serverId,
        subscriptionId, // is this with underscore???(where the unicast goes?)
        method_id: methodId,
        arguments: args,
        arguments_kv: argsKv,
        context,
        flags
    } = request;
    let callee = byId(state, serverId);
    if (callee) {
        callee = produce(callee, (c) => {
            c[DomainKeys.Agm]!.interests = c[DomainKeys.Agm]!.interests || {} as Record<SubscriptionId, Interest>;
            c[DomainKeys.Agm]!.interests[subscriptionId] = {subscriber: peerId, method_id: methodId};
        });
        const calleeSrc = callee.source;
        const method = callee[DomainKeys.Agm]!.methods![serverId][methodId];
        // todo feature metric
        return [
            setPeer(state, serverId, callee),
            isLocalSource(calleeSrc)
                ? [msg.addInterest(calleeSrc, subscriptionId, serverId, methodId, peerId, args, argsKv, flags, context)]
                : [unicast(peerToAddress(nodeId(state.ids), peerId), nodeToAddress(calleeSrc['node']), request as Subscribe)]];
    }
    throw exceptionInfo(`unable to find server with id ${serverId}`, {});
}

function subscribe(state: State, request: SubscribeRequest, subscriptionId: SubscriptionId): HandleReturnType {
    const {request_id: requestId, peer_id: peerId, server_id: serverId, method_id: methodId} = request;
    const caller = produce(byIdInDomainThrows(state, peerId, DomainKeys.Agm), (draft: Draft<AgmPeer>) => {
        draft[DomainKeys.Agm].subscriptions = draft[DomainKeys.Agm].subscriptions || {};
        draft[DomainKeys.Agm].subscriptions![subscriptionId] = {
            server: serverId,
            method_id: methodId,
            request_id: requestId
        };
    });
    const callee = byIdInDomainThrows(state, serverId, DomainKeys.Agm);
    validate(caller, callee, request);

    // todo log large msg
    // todo log action
    const message: RemoteSubscribeRequest = {...request as Subscribe, subscriptionId};
    return addInterest(caller, setPeer(state, peerId, caller), message);
}

function localSubscribe(state: State, source: Source, request): HandleReturnType {
    const [newIds, subscriptionId] = requestId(state.ids);
    return subscribe({...state, ids: newIds}, request, subscriptionId);
}

function remoteSubscribe(state: State, request: RemoteSubscribeRequest): HandleReturnType {
    const {subscriptionId, server_id: serverId} = request;
    return byId(state, serverId) ? subscribe(state, request, subscriptionId) : [state, []];
}

export function forwardSubscribe(state: State, source: Source, request: SubscribeRequest): HandleReturnType {
    return isLocalSource(source) ? localSubscribe(state, source, request) : remoteSubscribe(state, request as RemoteSubscribeRequest);
}

function dropSubscription(state: State, subscriber: Peer | undefined, subscriberId, subscriptionId: SubscriptionId, reason, ctx, isError: boolean): HandleReturnType {
    if (subscriber) {
        const subscription: Subscription | undefined = subscriber[DomainKeys.Agm]?.subscriptions?.[subscriptionId];
        const requestId = subscription?.request_id;
        const subSrc = subscriber.source;

        // todo log action

        return [setPeer(state, subscriberId, produce(subscriber, (s: Draft<Peer>) => {
            const d = s[DomainKeys.Agm]!;
            if (d.subscriptions) {
                delete d.subscriptions[subscriptionId];
                if (Object.keys(d.subscriptions).length === 0) {
                    delete d.subscriptions;
                }
            }
        })),
            isLocalSource(subSrc)
                ? isError
                    ? [error(agmDomainUri, subSrc, requestId, subscriberId, reason, ctx)]
                    : [msg.subscriptionCancelled(subSrc, subscriptionId, subscriberId, reason)]
                : []]
    } else {
        return [state, []];
    }
}

const log = getLogger('gateway.domains.agm.subscriptions');

/*Marks a request from a given subscriber as successful as a result of an accepted message.
  If the subscriber is a local one - it receives a direct message. Otherwise a message for the
  specific peer is sent to the cluster*/
function subscribed(state: State, subscriberId: PeerId, subscriptionId: SubscriptionId, streamId: StreamId, request: Accepted): HandleReturnType {
    const subscriber = byId(state, subscriberId);
    if (subscriber) {
        const requestId = subscriber[DomainKeys.Agm]?.subscriptions?.[subscriptionId]?.request_id;
        state = setPeer(state, subscriberId, produce(subscriber, (s) => {
            s[DomainKeys.Agm]!.subscriptions![subscriptionId].stream = streamId;
        }));
        const subSrc = subscriber.source;
        // todo log action
        if (isLocalPeer(subscriber)) {
            return [state, [msg.subscribed(subSrc, requestId, subscriberId, subscriptionId)]];
        } else {
            return [state, [unicast(peerToAddress(nodeId(state.ids), request.peer_id), nodeToAddress(subSrc['node']), request)]];
        }
    } else {
        return [state, []];
    }
}

/*Handles an 'accepted' message.
  The returned stream is associated with the subscriber and the interest*/
function accepted(state: State, peer: AgmPeer, request: AcceptedRequest): HandleReturnType {
    const {subscription_id: subscriptionId, peer_id: peerId, stream_id: streamId} = request;
    if (!streamId) {
        throwReason(agmInvalidSubscription, 'Invalid or missing stream id');
    }
    const interest = peer[DomainKeys.Agm].interests?.[subscriptionId];
    if (interest) {
        const subscriberId = interest.subscriber;
        state = setPeer(state, peerId, produce(peer, (p) => {
            const domain = p[DomainKeys.Agm];
            domain.interests = domain.interests || {};
            domain.interests[subscriptionId] = domain.interests[subscriptionId] || {};
            domain.interests[subscriptionId].stream = streamId;
            domain.streams = domain.streams || {};
            domain.streams[streamId] = domain.streams[streamId] || {};
            domain.streams[streamId][subscriberId] = domain.streams[streamId][subscriberId] || new Set<SubscriptionId>();
            domain.streams[streamId][subscriberId].add(subscriptionId);
        }));
        return subscribed(state, subscriberId, subscriptionId, streamId, request as Accepted);
    } else {
        if (log.enabledFor('debug')) {
            log.debug(`Subscription accept response ${JSON.stringify(request)} for missing interest`);
        }
        return [state, []] as HandleReturnType;
    }
}

/*Handles a subscription accept response coming from a remote peer*/
function remoteAccepted(state: State, request): HandleReturnType {
    const {subscription_id: subscriptionId, peer_id: peerId, subscriber_id: subscriberId} = request;
    const peer = byIdInDomain(state, peerId, DomainKeys.Agm);
    if (peer) {
        return accepted(state, peer, request);
    } else {
        log.warn(`Subscription accept response ${JSON.stringify(request)} from missing peer`);
        return dropSubscription(state, byId(state, subscriberId), subscriberId, subscriptionId, reason(agmSubscriptionFailure, 'Received a response from a missing server'), null, true);
    }
}

/*Handles a subscription accept response coming from a local peer*/
function localAccepted(state: State, source: Source, request): HandleReturnType {
    const {subscription_id, peer_id: peerId} = request;
    const peer = byIdInDomainThrows(state, peerId, DomainKeys.Agm);
    return accepted(state, peer, request);
}

export function forwardAccepted(state: State, source: Source, request: AcceptedRequest) {
    return isLocalSource(source) ? localAccepted(state, source, request) : remoteAccepted(state, request);
}

function removeInterestAndStream(subscriptionId: SubscriptionId, subscriberId: PeerId, streamId?): (p: Draft<Peer>) => void {
    return (p: Draft<Peer>) => {
        const d = p[DomainKeys.Agm];
        if (d) {
            if (d.interests) {
                delete d.interests[subscriptionId];
                if (Object.keys(d.interests).length === 0) {
                    delete d.interests;
                }
            }
            if (streamId) {
                if (d.streams) {
                    d.streams[streamId][subscriberId].delete(subscriptionId);
                    if (d.streams[streamId][subscriberId].size === 0) {
                        delete d.streams[streamId][subscriberId];
                        if (Object.keys(d.streams[streamId]).length === 0) {
                            delete d.streams[streamId];
                            if (Object.keys(d.streams).length === 0) {
                                delete d.streams;
                            }
                        }
                    }
                }
            }
        }
    }
}

export function dropInterest(state: State, source: Source, request: DropSubscriptionRequest/* | ErrorMessage*/, isError: boolean): HandleReturnType {
    const peerId = request.peer_id;
    const subscriptionId = isError ? request['request_id'] : request.subscription_id;
    const peer = byIdInDomainThrows(state, peerId, DomainKeys.Agm);

    const interest: Interest | undefined = peer[DomainKeys.Agm].interests?.[subscriptionId];
    if (interest) {
        const subscriberId = interest.subscriber;
        const streamId = interest.stream;
        const subscriber = byId(state, subscriberId);
        const subscriberSrc = subscriber!.source;
        if (streamId || isError) {
            state = setPeer(state, peerId, produce(peer, removeInterestAndStream(subscriptionId, subscriberId, streamId)));
            let m : readonly SendMessageAction<MessageType | CommandType>[];
            [state, m] = dropSubscription(state, subscriber, subscriberId, subscriptionId, requestToReason(request), request['context'], isError);
            if (subscriber && !isLocalSource(subscriberSrc)) {
                m = m.concat(unicast(peerToAddress(nodeId(state.ids), peerId), nodeToAddress(subscriberSrc['node']), request as DropSubscription));
            }
            return [state, m];
        } else {
            return [state, [error(agmDomainUri, source, subscriptionId, peerId, reasonBadDrop)]]
        }
    } else {
        return [state, [error(agmDomainUri, source, subscriptionId, peerId, reason(agmInvalidSubscription, 'Trying to drop a non-existing subscription'))]]
    }
}

function removeInterest(state: State, subscriber, serverId: PeerId, subscriptionId, request): HandleReturnType | undefined {
    const server = byId(state, serverId);
    if (server) {
        const interest = server[DomainKeys.Agm]?.interests?.[subscriptionId];
        if (interest) {
            //todo features
            const subscriberId = subscriber.id;
            const serverSrc = server.source;
            state = setPeer(state, serverId, produce(server, removeInterestAndStream(subscriptionId, subscriberId, interest.stream)));

            return [state, [isLocalSource(serverSrc) ? msg.removeInterest(serverSrc, subscriptionId, serverId, interest.method_id, subscriberId, requestToReason(request))
                : unicast(peerToAddress(nodeId(state.ids), subscriberId), nodeToAddress(serverSrc['node']), request)]];
        }
    }
}

function unsubscribe(state: State, request, subscriberId: PeerId, subscriptionId: SubscriptionId, requestId: string): HandleReturnType {
    const subscriber = byIdInDomainThrows(state, subscriberId, DomainKeys.Agm);
    const serverId = subscriber[DomainKeys.Agm].subscriptions?.[subscriptionId]?.server;
    // todo log action
    if (!serverId) {
        throwReason(agmInvalidSubscription,
            `Unable to find subscription with id ${subscriptionId} on subscriber id ${subscriberId}`);
    }

    state = setPeer(state, subscriberId, produce(subscriber, (s: Draft<AgmPeer>) => {
        const d = s[DomainKeys.Agm];
        if (d.subscriptions) {
            delete d.subscriptions[subscriptionId];
            if (Object.keys(d.subscriptions).length === 0) {
                delete d.subscriptions;
            }
        }
    }));
    const result = removeInterest(state, subscriber, serverId!, subscriptionId, request);
    return result || [state, []];
}

/**
 * Processes a remote unsubscription request.
 * Returns [state [messages]]
 */
function remoteUnsubscribe(state: State, request): HandleReturnType {
    const {request_id: requestId, peer_id: peerId, subscription_id: subscriptionId} = request;
    return unsubscribe(state, request, peerId, subscriptionId, requestId);
}

/**
 * Processes a local unsubscription request.
 * Returns [state [messages]]
 */
function localUnsubscribe(state: State, source: Source, request): HandleReturnType {
    const {request_id: requestId, peer_id: peerId, subscription_id: subscriptionId} = request;
    let messages;
    [state, messages] = unsubscribe(state, request, peerId, subscriptionId, requestId);
    return [state, messages.concat(success(agmDomainUri, source, requestId, peerId))] as HandleReturnType;
}

export function forwardUnsubscribe(state: State, source: Source, request: UnsubscribeRequest): HandleReturnType {
    return isLocalSource(source) ? localUnsubscribe(state, source, request) : remoteUnsubscribe(state, request);
}

export function forwardPublish(state: State, source: Source, request: PublishRequest): HandleReturnType {
    const {peer_id: serverId, stream_id: streamId, sequence: sqn, snapshot: isSnapshot, data} = request;
    // todo large msg
    const server = byIdInDomain(state, serverId, DomainKeys.Agm);
    const destinations: StreamPeerInfo = server?.[DomainKeys.Agm].streams?.[streamId] || {};
    const remotes = new Set<string>();
    return [state, Object.entries(destinations).flatMap(([subscriberId, subscriptions]: [PeerId, ReadonlySet<SubscriptionId>]) => {
        const subscriber = byId(state, subscriberId);
        const subSource = subscriber!.source;
        let msgs: Array<SendMessageAction<MessageType>> = [];
        if (isLocalSource(subSource)) {
            //todo trace

            msgs = Array.from(subscriptions, (subscriptionId) => {
                return msg.event(subSource, subscriberId, subscriptionId, false, sqn, isSnapshot, data);
            });
        } else {
            // remote source - we need to propagate the request to a unique list of nodes
            // otherwise there will be event duplication
            const node = subSource['node'];
            if (!remotes.has(node)) {
                remotes.add(node);
                msgs = [unicast<'publish'>(peerToAddress(nodeId(state.ids), serverId), nodeToAddress(node), request as Publish)];
            }
        }
        return msgs;
    })];
}

export function forwardPost(state: State, source: Source, request: PostRequest): HandleReturnType {
    const {peer_id: serverId, subscription_id: subscriptionId, sequence: sqn, snapshot: isSnapshot, data} = request;

    const server = byIdInDomain(state, serverId, DomainKeys.Agm);
    const subscriberId = server?.[DomainKeys.Agm].interests?.[subscriptionId].subscriber;
    const subscriber = byIdInDomain(state, subscriberId, DomainKeys.Agm);
    // todo large msg
    const subSrc = subscriber?.source;
    if (subSrc) {
        let messages: Array<SendMessageAction<MessageType>>;
        if (isLocalPeer(subscriber)) {
            messages = [msg.event(subSrc, subscriberId!, subscriptionId, true, sqn, isSnapshot, data)];
        } else {
            const recipient: NodeAddress = {
                type: 'node',
                node: subSrc['node']
            };
            messages = [unicast(peerToAddress(nodeId(state.ids), serverId), recipient, request as Post)];
        }
        return [state, messages];
    }
    return [state, []];
}
